import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from "../components/SEO";
import { PAGE_SEO } from "../seo-data";
import { openBookingModal } from "../components/BookingModal";
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, Bot, User, Sparkles, ArrowRight, 
  Calendar, CheckCircle2, Loader2, MessageSquare,
  Zap, Phone, Mail, Filter, UserCheck, Bell, Settings,
  Play, Pause, RotateCcw, ChevronRight
} from 'lucide-react';

// Icon mapping for workflow steps
const iconMap = {
  zap: Zap,
  message: MessageSquare,
  bot: Bot,
  calendar: Calendar,
  check: CheckCircle2,
  phone: Phone,
  mail: Mail,
  filter: Filter,
  user: UserCheck,
  bell: Bell,
  settings: Settings
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Personalized Workflow Visualization Component
const PersonalizedWorkflow = ({ workflowData }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  const colorClasses = {
    pink: {
      bg: 'bg-gradient-to-br from-pink-500 to-rose-500',
      text: 'text-pink-500',
      glow: 'shadow-[0_0_30px_rgba(236,72,153,0.3)]',
      line: 'bg-gradient-to-b from-pink-500 to-pink-300',
      pulse: 'bg-pink-500',
      statBg: 'bg-pink-500/10',
      statText: 'text-pink-400',
    },
    cyan: {
      bg: 'bg-gradient-to-br from-cyan-500 to-blue-500',
      text: 'text-cyan-500',
      glow: 'shadow-[0_0_30px_rgba(6,182,212,0.3)]',
      line: 'bg-gradient-to-b from-cyan-500 to-cyan-300',
      pulse: 'bg-cyan-500',
      statBg: 'bg-cyan-500/10',
      statText: 'text-cyan-400',
    },
    purple: {
      bg: 'bg-gradient-to-br from-purple-500 to-violet-500',
      text: 'text-purple-500',
      glow: 'shadow-[0_0_30px_rgba(139,92,246,0.3)]',
      line: 'bg-gradient-to-b from-purple-500 to-purple-300',
      pulse: 'bg-purple-500',
      statBg: 'bg-purple-500/10',
      statText: 'text-purple-400',
    },
  };

  const colors = colorClasses[workflowData.color_theme] || colorClasses.purple;
  const steps = workflowData.steps || [];

  // Auto-play animation
  useEffect(() => {
    let interval;
    if (isPlaying && steps.length > 0) {
      interval = setInterval(() => {
        setActiveStep(prev => {
          if (prev >= steps.length - 1) {
            return prev;
          }
          return prev + 1;
        });
      }, 1200);
    }
    return () => clearInterval(interval);
  }, [isPlaying, steps.length]);

  const handleReset = () => {
    setActiveStep(0);
    setIsPlaying(true);
  };

  return (
    <motion.div 
      className="pw-container"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="personalized-workflow"
    >
      <SEO {...PAGE_SEO.quiz} />
      {/* Header */}
      <div className="pw-header">
        <div className={`pw-icon ${colors.bg}`}>
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <h2 className="pw-title">{workflowData.workflow_title}</h2>
        <p className="pw-subtitle">Built specifically for your {workflowData.business_type} business</p>
      </div>

      {/* Workflow Visualization */}
      <div className="pw-workflow">
        {/* Progress Track */}
        <div className="pw-track">
          <motion.div 
            className={`pw-track-fill ${colors.bg}`}
            initial={{ height: '0%' }}
            animate={{ height: `${((activeStep + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>

        {/* Steps */}
        <div className="pw-steps">
          {steps.map((step, index) => {
            const StepIcon = iconMap[step.icon] || Zap;
            const isActive = index <= activeStep;
            const isCurrent = index === activeStep;

            return (
              <motion.div
                key={step.title || `step-${index}`}
                className={`pw-step ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
              >
                {/* Step Node */}
                <div className={`pw-node ${isCurrent ? colors.bg + ' ' + colors.glow : ''}`}>
                  {isActive ? (
                    <StepIcon className="w-5 h-5" />
                  ) : (
                    <span className="pw-num">{index + 1}</span>
                  )}
                  {isCurrent && (
                    <motion.div 
                      className={`pw-pulse ${colors.pulse}`}
                      animate={{ scale: [1, 2], opacity: [0.5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                  )}
                </div>

                {/* Step Content */}
                <motion.div 
                  className="pw-content"
                  animate={{ opacity: isActive ? 1 : 0.3 }}
                >
                  <h4 className={`pw-step-title ${isCurrent ? colors.text : ''}`}>
                    {step.title}
                  </h4>
                  <p className="pw-step-desc">{step.description}</p>
                </motion.div>

                {/* Connector */}
                {index < steps.length - 1 && (
                  <div className="pw-connector">
                    <motion.div 
                      className={`pw-connector-fill ${colors.line}`}
                      initial={{ height: '0%' }}
                      animate={{ height: isActive ? '100%' : '0%' }}
                      transition={{ duration: 0.4, delay: 0.2 }}
                    />
                  </div>
                )}
              </motion.div>
            );
          })}

          {/* Outcome */}
          <motion.div 
            className={`pw-outcome ${activeStep >= steps.length - 1 ? 'active' : ''}`}
            animate={{ 
              opacity: activeStep >= steps.length - 1 ? 1 : 0.3,
              scale: activeStep >= steps.length - 1 ? 1 : 0.9
            }}
          >
            <div className="pw-outcome-badge">
              <CheckCircle2 className="w-6 h-6 text-green-500" />
              <span className="pw-outcome-text">{workflowData.outcome}</span>
            </div>
          </motion.div>
        </div>

        {/* Controls */}
        <div className="pw-controls">
          <button className="pw-control-btn" onClick={handleReset}>
            <RotateCcw className="w-4 h-4" />
          </button>
          <button 
            className={`pw-control-btn pw-play-btn ${isPlaying ? 'playing' : ''}`}
            onClick={() => setIsPlaying(!isPlaying)}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <span className="pw-step-indicator">
            Step {activeStep + 1} of {steps.length}
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className="pw-stats">
        <div className={`pw-stat ${colors.statBg}`}>
          <span className={`pw-stat-value ${colors.statText}`}>
            {workflowData.stats?.response_time || '< 60s'}
          </span>
          <span className="pw-stat-label">Response Time</span>
        </div>
        <div className={`pw-stat ${colors.statBg}`}>
          <span className={`pw-stat-value ${colors.statText}`}>
            +{workflowData.stats?.conversion_boost || '40%'}
          </span>
          <span className="pw-stat-label">Conversion Boost</span>
        </div>
        <div className={`pw-stat ${colors.statBg}`}>
          <span className={`pw-stat-value ${colors.statText}`}>
            {workflowData.stats?.time_saved || '40+ hrs'}
          </span>
          <span className="pw-stat-label">Time Saved</span>
        </div>
      </div>

      {/* Personalized Message */}
      <div className="pw-message">
        <p>{workflowData.personalized_message}</p>
      </div>

      {/* CTA */}
      <div className="pw-cta">
        <button onClick={openBookingModal} className="pw-cta-primary" data-testid="workflow-cta-get-started">
          <Calendar className="w-5 h-5" />
          Book Your Demo Now
          <ChevronRight className="w-5 h-5" />
        </button>
        <button 
          className="pw-cta-secondary"
          onClick={() => window.location.reload()}
        >
          <MessageSquare className="w-5 h-5" />
          Start Over
        </button>
      </div>
    </motion.div>
  );
};

const AIDemo = () => {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const [workflowData, setWorkflowData] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start the AI demo session
  useEffect(() => {
    const startSession = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ai-demo/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSessionId(data.session_id);
          setMessages([{
            role: 'assistant',
            content: data.initial_message
          }]);
        } else {
          // Fallback greeting if API fails
          setMessages([{
            role: 'assistant',
            content: "Hi there! 👋 I'm Skye, your AI assistant. I'd love to learn about your business and show you how we can help you convert more leads. What type of business do you run?"
          }]);
          setSessionId('fallback-' + Date.now());
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to start session:', error);
        // Fallback greeting
        setMessages([{
          role: 'assistant',
          content: "Hi there! 👋 I'm Skye, your AI assistant. I'd love to learn about your business and show you how we can help you convert more leads. What type of business do you run?"
        }]);
        setSessionId('fallback-' + Date.now());
      } finally {
        setIsStarting(false);
      }
    };

    startSession();
  }, []);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message to chat
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ai-demo/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage,
          conversation_history: messages
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessages([...newMessages, { 
          role: 'assistant', 
          content: data.response 
        }]);
        
        if (data.is_complete && data.recommendation?.workflow) {
          setIsComplete(true);
          setWorkflowData(data.recommendation.workflow);
        }
      } else {
        throw new Error('API error');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Chat error:', error);
      setMessages([...newMessages, { 
        role: 'assistant', 
        content: "I apologize, but I'm having trouble connecting right now. Please try again or book a demo directly!" 
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const quickResponses = [
    "Real estate",
    "Home services",
    "Dental/Medical",
    "Coaching/Consulting"
  ];

  const handleQuickResponse = (response) => {
    setInputValue(response);
    setTimeout(() => {
      const form = document.querySelector('.aid-input-form');
      if (form) form.requestSubmit();
    }, 100);
  };

  return (
    <div className="ai-demo-page" data-testid="ai-demo-page">
      {/* Hero */}
      <section className="aid-hero">
        <div className="aid-hero-bg">
          <div className="aid-grid"></div>
          <div className="aid-glow aid-glow-1"></div>
          <div className="aid-glow aid-glow-2"></div>
        </div>
        
        <div className="container">
          <motion.div 
            className="aid-hero-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="aid-badge">
              <Sparkles className="w-4 h-4" />
              <span>AI-Powered Demo</span>
            </div>
            <h1 className="aid-title">
              Get Your <span className="aid-gradient">Personalized</span> Demo
            </h1>
            <p className="aid-subtitle">
              Chat with our AI to discover exactly how Skye AI can transform your lead conversion.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Chat Interface OR Personalized Workflow */}
      <section className="aid-chat-section">
        <div className="container">
          {isComplete && workflowData ? (
            // Show Personalized Workflow Visualization
            <PersonalizedWorkflow workflowData={workflowData} />
          ) : (
            // Show Chat Interface
            <div className="aid-chat-container">
              {/* Chat Window */}
              <div className="aid-chat-window">
                {/* Messages */}
                <div className="aid-messages">
                  {isStarting ? (
                    <div className="aid-loading-state">
                      <Loader2 className="w-8 h-8 animate-spin" />
                      <p>Starting your personalized demo...</p>
                    </div>
                  ) : (
                    <AnimatePresence>
                      {messages.map((msg, index) => (
                        <motion.div
                          key={`${msg.role}-${msg.content.slice(0, 20)}-${index}`}
                          className={`aid-message ${msg.role}`}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="aid-message-avatar">
                            {msg.role === 'assistant' ? (
                              <Bot className="w-5 h-5" />
                            ) : (
                              <User className="w-5 h-5" />
                            )}
                          </div>
                          <div className="aid-message-content">
                            <div className="aid-message-text">
                              {msg.content.split('\n').map((line, i) => (
                                <React.Fragment key={i}>
                                  {line.startsWith('**') && line.endsWith('**') ? (
                                    <strong>{line.replace(/\*\*/g, '')}</strong>
                                  ) : line.startsWith('- ') ? (
                                    <li>{line.substring(2)}</li>
                                  ) : (
                                    <p>{line}</p>
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                      
                      {isLoading && (
                        <motion.div
                          className="aid-message assistant"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                        >
                          <div className="aid-message-avatar">
                            <Bot className="w-5 h-5" />
                          </div>
                          <div className="aid-message-content">
                            <div className="aid-typing">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Quick Responses (show only at start) */}
                {messages.length === 1 && !isLoading && (
                  <motion.div 
                    className="aid-quick-responses"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <p className="aid-quick-label">Quick responses:</p>
                    <div className="aid-quick-buttons">
                      {quickResponses.map((response) => (
                        <button
                          key={response}
                          className="aid-quick-btn"
                          onClick={() => handleQuickResponse(response)}
                        >
                          {response}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Input Area */}
                <form className="aid-input-form" onSubmit={sendMessage}>
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Type your message..."
                    className="aid-input"
                    disabled={isLoading || isStarting}
                  />
                  <button 
                    type="submit" 
                    className="aid-send-btn"
                    disabled={!inputValue.trim() || isLoading || isStarting}
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </form>
              </div>

              {/* Side Panel */}
              <div className="aid-side-panel">
                <div className="aid-side-card">
                  <div className="aid-side-icon">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <h3>How This Works</h3>
                  <ul className="aid-side-list">
                    <li>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Tell us about your business</span>
                    </li>
                    <li>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Share your lead challenges</span>
                    </li>
                    <li>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Get a personalized workflow</span>
                    </li>
                  </ul>
                </div>

                <div className="aid-side-stats">
                  <div className="aid-stat-item">
                    <span className="aid-stat-value">2 min</span>
                    <span className="aid-stat-label">Average time</span>
                  </div>
                  <div className="aid-stat-item">
                    <span className="aid-stat-value">100%</span>
                    <span className="aid-stat-label">Personalized</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AIDemo;
